import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppNav, { NAV_SAFE_CLASS } from "@/components/nav/AppNav";
import { Bell, MapPin, Ghost, ArrowLeft, Loader2, LogOut, Volume2, Phone, Settings as SettingsIcon, BellRing } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MadeByBadge from "@/components/brand/MadeByBadge";
import {
  isPushSupported,
  isPushSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  currentPermission,
} from "@/lib/pushNotifications";

function Toggle({ active, onChange, testid, disabled }) {
  return (
    <button
      role="switch"
      aria-checked={active}
      onClick={() => !disabled && onChange(!active)}
      data-testid={testid}
      disabled={disabled}
      className={`relative w-14 h-8 border-hard-2 rounded-full transition-colors ${active ? "bg-[#CCFF00]" : "bg-white"} ${disabled ? "opacity-50" : "press-btn"}`}
    >
      <span
        className={`absolute top-0.5 w-6 h-6 rounded-full border-[2px] border-[#0A0A0A] bg-white transition-transform ${active ? "translate-x-6" : "translate-x-0.5"}`}
      />
    </button>
  );
}

function Row({ icon: Icon, title, desc, right, color, testid }) {
  return (
    <div className="flex items-center gap-3 p-3 border-hard-2 rounded-2xl bg-white shadow-hard-sm" data-testid={testid}>
      <div className="w-11 h-11 shrink-0 rounded-xl border-hard-2 flex items-center justify-center" style={{ background: color }}>
        <Icon strokeWidth={3} className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-black text-sm leading-tight">{title}</div>
        <div className="text-[11px] text-[#555] leading-snug">{desc}</div>
      </div>
      {right}
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [geoStatus, setGeoStatus] = useState("");
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("yappo-notif-sound") !== "0"; } catch { return true; }
  });
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState("");

  useEffect(() => {
    (async () => {
      if (isPushSupported()) setPushOn(await isPushSubscribed());
    })();
  }, []);

  const togglePush = async (v) => {
    setPushMsg("");
    if (!isPushSupported()) {
      setPushMsg("Bu tarayıcı web push desteklemiyor. Android'de Chrome, masaüstünde Chrome/Edge kullan.");
      return;
    }
    setPushBusy(true);
    try {
      if (v) {
        const res = await subscribeToPush();
        if (res.ok) {
          setPushOn(true);
          setPushMsg("Bildirimler açıldı ✓");
        } else if (res.reason === "permission") {
          setPushMsg(currentPermission() === "denied"
            ? "Tarayıcı bildirim iznini engellemiş — adres çubuğundaki 🔒 ikonundan izin ver."
            : "İzin verilmedi.");
        } else if (res.reason === "no-key") {
          setPushMsg("Sunucu push için yapılandırılmamış. Lütfen deploy'un güncel olduğundan emin ol.");
        } else if (res.reason === "unsupported") {
          setPushMsg("Bu tarayıcı web push desteklemiyor.");
        }
      } else {
        await unsubscribeFromPush();
        setPushOn(false);
        setPushMsg("Bildirimler kapatıldı");
      }
    } finally {
      setPushBusy(false);
    }
  };

  const toggleSound = (v) => {
    setSoundOn(v);
    try { localStorage.setItem("yappo-notif-sound", v ? "1" : "0"); } catch {}
    if (v) {
      // Immediate audible feedback so the user knows the sound works.
      try {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return;
        const ctx = new Ctor();
        const now = ctx.currentTime;
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.setValueAtTime(880, now);
        o.frequency.exponentialRampToValueAtTime(520, now + 0.18);
        g.gain.setValueAtTime(0.0001, now);
        g.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
        o.connect(g).connect(ctx.destination); o.start(now); o.stop(now + 0.35);
        setTimeout(() => { try { ctx.close(); } catch {} }, 500);
      } catch {}
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/me/settings");
        setSettings(data);
      } catch (e) { setError(formatApiError(e)); }
      setLoading(false);
    })();
  }, []);

  const patch = async (updates) => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/users/me/settings", updates);
      setSettings(data);
    } catch (e) { setError(formatApiError(e)); }
    setSaving(false);
  };

  const toggleLocationSharing = async (on) => {
    if (on && typeof navigator !== "undefined" && navigator.geolocation) {
      // Trigger browser permission prompt immediately for better UX
      setGeoStatus("Konum izni isteniyor…");
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          setGeoStatus("");
          await patch({ location_sharing: true });
          try {
            await api.post("/location/ping", {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            });
          } catch {}
        },
        (err) => {
          setGeoStatus(err.code === 1
            ? "Konum izni reddedildi — tarayıcı ayarlarından açman gerekiyor"
            : "Konum alınamadı");
        },
        { timeout: 15000, enableHighAccuracy: false, maximumAge: 60000 }
      );
    } else {
      await patch({ location_sharing: on });
      setGeoStatus("");
    }
  };

  const updateNotif = async (key, value) => {
    const next = { ...(settings.notif_prefs || {}), [key]: value };
    await patch({ notif_prefs: next });
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const np = settings.notif_prefs || {};

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="settings-page">
      <AppNav />
      <div className={NAV_SAFE_CLASS}>
        <div className="max-w-2xl mx-auto p-4 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => navigate(-1)}
              data-testid="settings-back-btn"
              className="w-10 h-10 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm"
              aria-label="Geri"
            >
              <ArrowLeft strokeWidth={3} className="w-4 h-4" />
            </button>
            <h1 className="font-display text-3xl md:text-4xl">Ayarlar</h1>
          </div>

          {error && (
            <div className="border-hard-2 bg-[#FFD0C2] rounded-xl px-3 py-2 text-sm font-bold mb-4" data-testid="settings-error">{error}</div>
          )}

          {/* Location */}
          <div className="space-y-3 mb-5">
            <div className="text-[11px] uppercase font-black tracking-widest text-[#555] px-1">Konum & Gizlilik</div>
            <Row
              testid="row-location-sharing"
              icon={MapPin}
              title="Konum paylaşımı"
              desc="Arkadaşların canlı konumunu haritada görebilsin"
              color="#CEECFF"
              right={<Toggle active={settings.location_sharing} onChange={toggleLocationSharing} testid="toggle-location-sharing" />}
            />
            <Row
              testid="row-ghost-mode"
              icon={Ghost}
              title="Hayalet modu"
              desc="Konumun kaydedilir ama arkadaşlarına gizli kalır"
              color="#E6E1F5"
              right={<Toggle active={settings.ghost_mode} onChange={(v) => patch({ ghost_mode: v })} testid="toggle-ghost-mode" disabled={!settings.location_sharing} />}
            />
            {geoStatus && (
              <div className="text-xs font-bold text-[#555] px-1" data-testid="geo-status">{geoStatus}</div>
            )}
          </div>

          {/* Notifications */}
          <div className="space-y-3 mb-5">
            <div className="text-[11px] uppercase font-black tracking-widest text-[#555] px-1">Bildirimler</div>
            <Row
              testid="row-push-notifications"
              icon={BellRing}
              title="Uygulama kapalıyken de bildirim"
              desc={
                !isPushSupported()
                  ? "Tarayıcın web push desteklemiyor"
                  : pushOn
                    ? "Bildirimler açık — mesajlar sekme kapalıyken de gelir"
                    : "Aç → tarayıcı izin isteyecek, sonra yappo.chat kapalıyken bile mesajlar bildirim olarak gelir"
              }
              color="#CCFF00"
              right={
                <Toggle
                  active={pushOn}
                  onChange={togglePush}
                  testid="toggle-push-notifications"
                  disabled={pushBusy || !isPushSupported()}
                />
              }
            />
            {pushMsg && (
              <div className="px-1 text-[11px] font-bold text-[#555]" data-testid="push-status-msg">{pushMsg}</div>
            )}
            <Row
              testid="row-notif-new-message"
              icon={Bell}
              title="Yeni mesaj"
              desc="Sekme kapalıyken bildirim gönder"
              color="#CCFF00"
              right={<Toggle active={np.new_message !== false} onChange={(v) => updateNotif("new_message", v)} testid="toggle-notif-new-message" />}
            />
            <Row
              testid="row-notif-friend-req"
              icon={Bell}
              title="Arkadaşlık isteği"
              desc="Yeni istek geldiğinde bildir"
              color="#D4B3FF"
              right={<Toggle active={np.friend_request !== false} onChange={(v) => updateNotif("friend_request", v)} testid="toggle-notif-friend-req" />}
            />
            <Row
              testid="row-notif-streak"
              icon={Bell}
              title="Seri hatırlatıcısı"
              desc="Görev serin risk altındayken hatırlat"
              color="#FF9E80"
              right={<Toggle active={np.streak_reminder !== false} onChange={(v) => updateNotif("streak_reminder", v)} testid="toggle-notif-streak" />}
            />
            <Row
              testid="row-notif-sound"
              icon={Volume2}
              title="Bildirim sesi"
              desc="Yeni istek/mesaj geldiğinde kısa bir ding çal"
              color="#CEECFF"
              right={<Toggle active={soundOn} onChange={toggleSound} testid="toggle-notif-sound" />}
            />
          </div>

          {/* Account */}
          <div className="space-y-3">
            <div className="text-[11px] uppercase font-black tracking-widest text-[#555] px-1">Hesap</div>
            <button
              onClick={() => navigate("/app/calls")}
              data-testid="settings-open-calls-btn"
              className="w-full flex items-center gap-3 p-3 border-hard-2 rounded-2xl bg-white press-btn shadow-hard-sm text-left"
            >
              <div className="w-11 h-11 shrink-0 rounded-xl border-hard-2 flex items-center justify-center bg-[#CCFF00]">
                <Phone strokeWidth={3} className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-black text-sm">Arama geçmişi</div>
                <div className="text-[11px] text-[#555]">Sesli/görüntülü arama kayıtları</div>
              </div>
            </button>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              data-testid="settings-logout-btn"
              className="w-full flex items-center gap-3 p-3 border-hard-2 rounded-2xl bg-white press-btn shadow-hard-sm text-left"
            >
              <div className="w-11 h-11 shrink-0 rounded-xl border-hard-2 flex items-center justify-center bg-[#FFD0C2]">
                <LogOut strokeWidth={3} className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-black text-sm">Çıkış yap</div>
                <div className="text-[11px] text-[#555]">Bu cihazdaki oturumu kapat</div>
              </div>
            </button>
            {user?.is_admin && (
              <button
                onClick={() => navigate("/app/admin")}
                data-testid="settings-open-admin-btn"
                className="w-full flex items-center gap-3 p-3 border-hard-2 rounded-2xl bg-white press-btn shadow-hard-sm text-left"
              >
                <div className="w-11 h-11 shrink-0 rounded-xl border-hard-2 flex items-center justify-center bg-white">
                  <SettingsIcon strokeWidth={3} className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-black text-sm">Gelişmiş</div>
                  <div className="text-[11px] text-[#555]">Yönetim seçenekleri</div>
                </div>
              </button>
            )}
          </div>

          {saving && <div className="mt-4 text-xs text-center text-[#555] font-bold"><Loader2 className="inline w-3 h-3 animate-spin mr-1" /> Kaydediliyor…</div>}

          {/* About / Maker */}
          <div className="mt-8 pt-6 border-t-2 border-dashed border-[#0A0A0A]/20">
            <div className="text-[11px] uppercase font-black tracking-widest text-[#555] px-1 mb-3">Uygulama Hakkında</div>
            <div className="border-hard-2 rounded-2xl bg-white p-4 shadow-hard-sm">
              <div className="text-sm font-black leading-tight">Yappo</div>
              <div className="text-[11px] text-[#555] mb-3">Sohbet gerçekten canlıysa · yappo.chat</div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[11px] text-[#333] font-bold">Yapımcı & geliştirici</div>
                <MadeByBadge />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
